import React from 'react';
import { motion, useInView } from 'framer-motion';
import { ScrambleText } from '../components/ui/ScrambleText';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowRight } from 'lucide-react';

const CareerCard = ({ title, description, delay = 0, isInternal }) => {
  const Icon = isInternal ? ArrowRight : ArrowUpRight;
  return (
    <motion.div
      className="relative p-6 rounded-xl border border-[#4F6A63]/10 bg-[#4F6A63]/5 hover:bg-[#4F6A63]/15 hover:scale-[1.02] hover:shadow-[0_0_15px_rgba(79,106,99,0.3)] transition-all duration-300 h-full group"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
    >
      <Icon
        className="absolute top-4 right-4 h-5 w-5 text-[#4F6A63] group-hover:text-[#16B08B] transition-colors duration-300"
        aria-hidden="true"
      />
      <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#16B08B] transition-colors duration-300 pr-8">
        <ScrambleText text={title} />
      </h3>
      <p className="text-sm text-[#4F6A63] font-mono leading-relaxed">
        <ScrambleText text={description} />
      </p>
    </motion.div>
  );
};

export default function Careers({ id }) {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { margin: '-50% 0px -50% 0px' });

  const careers = [
    {
      title: "AI Research Scientist",
      description:
        "For postdoctoral researchers from any discipline, not just CS, who are ready to explore how their ideas, theories, or methods can help shape the next primitives of intelligence.",
      link: "https://www.linkedin.com/jobs/view/4209315909",
    },
    {
      title: "AI Systems Engineer",
      description:
        "For engineers, designers, and systems thinkers who want to architect the future of intelligent computation.",
      link: "https://www.linkedin.com/jobs/view/4209318919",
    },
    {
      title: "AI Research Residency - visiting scholar",
      description:
        "For established researchers applying or advancing AI in any domain, ready to work alongside a high-agency research team to turn deep expertise into real-world impact.",
      link: "/careers/visiting-scholar",
    },
    {
      title: "AI Research Residency - internship",
      description:
        "For PhD students from any field working with AI who are eager to turn their ideas into impact with a fast-moving, interdisciplinary team.",
      link: "/careers/internship",
    },
  ];

  return (
    <motion.section
      id={id}
      ref={ref}
      className="px-6 flex justify-center items-center py-16 md:py-10 mt-40"
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
    >
      <div className="max-w-6xl mx-auto w-full pt-24">
        <h2 className="px-3 py-1 rounded-full text-xs font-bold bg-[#16B08B]/20 text-[#16B08B] uppercase mb-5 w-fit mx-auto">
          <ScrambleText text="CAREERS" enabled={isInView} />
        </h2>

        <motion.h2
          className="text-3xl md:text-4xl font-extrabold text-white leading-tight mb-5 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.4, ease: 'easeOut', delay: 0.2 }}
        >
          <ScrambleText text="Advance AI at Maincode" enabled={isInView} />
        </motion.h2>

        <motion.p
          className="text-base text-[#4F6A63] font-mono leading-loose text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.5, ease: 'easeOut', delay: 0.3 }}
        >
          <ScrambleText
            text="Whether you're a postdoc, a systems builder, or an academic on the edge, we have a place for you at the frontier."
            enabled={isInView}
          />
        </motion.p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-52">
          {careers.map((career, index) => {
            const isExternal = career.link && career.link.startsWith('http');
            const isInternal = !isExternal;

            const card = (
              <CareerCard
                title={career.title}
                description={career.description}
                delay={0.4 + index * 0.1}
                isInternal={isInternal}
              />
            );

            if (isExternal) {
              return (
                <a
                  key={career.title}
                  href={career.link}
                  className="block no-underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {card}
                </a>
              );
            } else {
              return (
                <Link
                  key={career.title}
                  to={career.link}
                  className="block no-underline"
                >
                  {card}
                </Link>
              );
            }
          })}
        </div>

        <motion.div
          className="flex justify-center"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.5, ease: "easeOut", delay: 0.8 }}
        />
      </div>
    </motion.section>
  );
}